import { useState, useEffect, useRef, useCallback } from 'react';
import pDebounce from 'p-debounce';

/* istanbul ignore file */

/* eslint-disable no-console */

//tod: 131051 Google Maps API Key - TODO: Consider moving to environment variable
const GOOGLE_MAPS_API_KEY = "AIzaSyC7-hA3o70PlUzXpP-fg--ki2jmCyRz-HQ";

export interface StreetViewData {
  location: { lat: number; lng: number };
  heading: number;
  pitch: number;
  zoom: number;
}

export interface GoogleMapsAPIHookReturn {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  updateLocation: (position: { lat: number; lng: number }) => void;
  geocodeAddress: (address: string) => Promise<{ lat: number; lng: number } | null>;
  generateStreetViewUrl: (data: StreetViewData) => string;
  parseStreetViewUrl: (url: string) => StreetViewData | null;
}

/**
 * Hook for managing Google Maps API loading and Street View utilities
 * 
 * @returns Object containing API state and utility functions
 */
export const useGoogleMapsAPI = (): GoogleMapsAPIHookReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Set mounted state for this hook
    isMountedRef.current = true;
    
    if ((window as any).google?.maps) {
      if (isMountedRef.current) {
        setIsLoaded(true);
      }
      return;
    }

    if (isLoading) return;

    if (isMountedRef.current) {
      setIsLoading(true);
    }
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (isMountedRef.current) {
        setIsLoaded(true);
        setIsLoading(false);
      }
    };
    
    script.onerror = () => {
      if (isMountedRef.current) {
        setError('Failed to load Google Maps API');
        setIsLoading(false);
      }
    };
    
    document.head.appendChild(script);

    // Cleanup timeout
    const timeout = setTimeout(() => {
      if (!(window as any).google?.maps && isMountedRef.current) {
        setError('Google Maps API loading timeout');
        setIsLoading(false);
      }
    }, 10000);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeout);
    };
  }, [isLoaded, isLoading]);

  // Additional cleanup effect for the hook itself
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Updates the location for Street View (can be extended for specific use cases)
   * Currently serves as a placeholder for future location update functionality
   */
  const updateLocation = useCallback((position: { lat: number; lng: number }) => {
    // This function can be used by components to trigger location updates
    // Implementation depends on specific use case
    console.log('Location update requested:', position);
  }, []);

  /**
   * Geocodes an address string to coordinates
   */
  const geocodeAddress = useCallback(async (address: string): Promise<{ lat: number; lng: number } | null> => {
    if (!(window as any).google?.maps) {
      throw new Error('Google Maps API not loaded');
    }

    return new Promise((resolve, reject) => {
      const geocoder = new (window as any).google.maps.Geocoder();
      const timeout = setTimeout(() => {
        reject(new Error("Geocoding timeout"));
      }, 5000);
      
      geocoder.geocode({ address }, (results: any, status: any) => {
        clearTimeout(timeout);
        
        if (status === "OK" && results && results.length > 0) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          // Validate coordinates
          if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
            resolve({ lat, lng });
          } else {
            resolve(null);
          }
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }, []);

  /**
   * Generates a Street View URL from data
   */
  const generateStreetViewUrl = useCallback((data: StreetViewData): string => {
    const baseUrl = `//maps.googleapis.com/maps/api/streetview`;
    const params = new URLSearchParams({
      size: "720x360",
      key: GOOGLE_MAPS_API_KEY,
      location: `${data.location.lat},${data.location.lng}`,
      heading: data.heading.toString(),
      pitch: data.pitch.toString(),
      fov: data.zoom.toString()
    });
    return `${baseUrl}?${params.toString()}`;
  }, []);

  /**
   * Parses Street View data from URL
   */
  const parseStreetViewUrl = useCallback((url: string): StreetViewData | null => {
    try {
      const urlObj = new URL(url.startsWith("//") ? `https:${url}` : url);
      const params = new URLSearchParams(urlObj.search);
      
      const location = params.get("location");
      const heading = params.get("heading");
      const pitch = params.get("pitch");
      const zoom = params.get("fov");

      if (location) {
        const [lat, lng] = location.split(",").map(Number);
        
        // Validate coordinates from URL
        if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
          return null;
        }
        
        return {
          location: { lat, lng },
          heading: heading ? Number(heading) : 0,
          pitch: pitch ? Number(pitch) : 0,
          zoom: zoom ? Number(zoom) : 90
        };
      }
    } catch (e) {
      // Failed to parse Street View URL
    }
    return null;
  }, []);

  return { 
    isLoaded, 
    isLoading, 
    error, 
    updateLocation,
    geocodeAddress,
    generateStreetViewUrl,
    parseStreetViewUrl
  };
};

/**
 * Utility function to check if Street View is available at a location
 */
export const checkStreetViewAvailability = async (
  position: { lat: number; lng: number },
  radius = 50
): Promise<boolean> => {
  if (!(window as any).google?.maps) {
    throw new Error('Google Maps API not loaded');
  }

  return new Promise((resolve) => {
    const streetViewService = new (window as any).google.maps.StreetViewService();
    const timeout = setTimeout(() => {
      resolve(false);
    }, 5000);

    streetViewService.getPanorama({
      location: position,
      radius
    }, (data: any, status: any) => {
      clearTimeout(timeout);
      resolve(status === (window as any).google.maps.StreetViewStatus.OK);
    });
  });
};

/**
 * Utility function to get the optimal panorama location and heading for an address
 */
export const getOptimalPanoramaData = async (
  targetPosition: { lat: number; lng: number }
): Promise<{ position: { lat: number; lng: number }; heading: number } | null> => {
  if (!(window as any).google?.maps) {
    throw new Error('Google Maps API not loaded');
  }

  try {
    const streetViewService = new (window as any).google.maps.StreetViewService();
    const originalPoint = new (window as any).google.maps.LatLng(targetPosition.lat, targetPosition.lng);
    
    const panoramaData = await new Promise<any>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Panorama lookup timeout"));
      }, 3000);
      
      streetViewService.getPanoramaByLocation(originalPoint, 100, (data: any, status: any) => {
        clearTimeout(timeout);
        if (status === (window as any).google.maps.StreetViewStatus.OK && data) {
          resolve(data);
        } else {
          reject(new Error(`Failed to get panorama data: ${status}`));
        }
      });
    });

    if (panoramaData?.location?.latLng) {
      const panoramaPoint = panoramaData.location.latLng;
      
      // Calculate heading from panorama position toward the original address point
      const calculatedHeading = (window as any).google.maps.geometry.spherical.computeHeading(
        panoramaPoint, 
        originalPoint
      );
      
      return {
        position: { 
          lat: panoramaPoint.lat(), 
          lng: panoramaPoint.lng() 
        },
        heading: calculatedHeading
      };
    }
  } catch (error) {
    console.log("Could not get optimal panorama data:", error);
  }

  return null;
};

/**
 * Initialize Street View with given parameters
 */
export const initializeStreetView = async (params: {
  apiLoaded: boolean;
  streetViewRef: React.RefObject<HTMLDivElement>;
  streetViewInstance: React.MutableRefObject<google.maps.StreetViewPanorama | null>;
  isInitializing: boolean;
  address: { postcode?: string; huisnummer?: string; straat?: string; plaats?: string };
  initializationInProgressRef: React.MutableRefObject<boolean>;
  isMountedRef: React.MutableRefObject<boolean>;
  initialUrl?: string;
  hasInitializedOnce: boolean;
  isAddressChange: boolean;
  parseStreetViewUrl: (url: string) => StreetViewData | null;
  generateStreetViewUrl: (data: StreetViewData) => string;
  geocodeAddress: (address: string) => Promise<{ lat: number; lng: number } | null>;
  onPositionChange: (url: string) => void;
  onAddressChanged?: () => void;
  safeSetIsInitializing: (value: boolean) => void;
  safeSetInitializationError: (value: string | null) => void;
  safeSetCurrentStreetViewUrl: (value: string | null) => void;
  setShowLoadingAfterDebounce: (value: boolean) => void;
}): Promise<google.maps.StreetViewPanorama | null> => {
  const {
    apiLoaded, streetViewRef, streetViewInstance, isInitializing, address,
    initializationInProgressRef, isMountedRef, initialUrl, hasInitializedOnce,
    isAddressChange, parseStreetViewUrl, generateStreetViewUrl, geocodeAddress,
    onPositionChange, onAddressChanged, safeSetIsInitializing, safeSetInitializationError,
    safeSetCurrentStreetViewUrl, setShowLoadingAfterDebounce
  } = params;

  console.log("initializeStreetView called", {
    apiLoaded,
    hasRef: !!streetViewRef.current,
    hasInstance: !!streetViewInstance.current,
    isInitializing,
    hasAddress: !!address.postcode && !!address.huisnummer
  });
  
  if (!apiLoaded || !streetViewRef.current || streetViewInstance.current || isInitializing || 
      !address.postcode || !address.huisnummer || initializationInProgressRef.current) {
    console.log("Skipping street view initialization due to state conditions");
    return null;
  }

  // Double-check if another initialization started while we were waiting
  if (streetViewInstance.current || isInitializing || initializationInProgressRef.current) {
    console.log("Aborting street view initialization - another initialization in progress");
    return null;
  }

  // Set flags to prevent concurrent initialization
  initializationInProgressRef.current = true;
  safeSetIsInitializing(true);
  safeSetInitializationError(null);
  setShowLoadingAfterDebounce(false);

  try {
    // Get initial position
    let position: { lat: number; lng: number } | null = null;
    
    // Try to parse initial position from URL (only on first initialization and not during address changes)
    if (initialUrl && !hasInitializedOnce && !isAddressChange) {
      console.log("Using initial URL for first load:", initialUrl);
      const initialData = parseStreetViewUrl(initialUrl);
      if (initialData) {
        console.log("Using position from URL:", initialData.location);
        position = initialData.location;
      }
    }
    
    // If no position from URL, geocode the current address
    if (!position) {
      try {
        const addressString = `${address.straat || ''} ${address.huisnummer || ''}, ${address.postcode || ''} ${address.plaats || ''}`.trim();
        
        // Check if we have enough address info for reliable geocoding
        if (!address.postcode || !address.huisnummer) {
          console.log("Address is incomplete for geocoding");
          position = { lat: 52.3676, lng: 4.9041 }; // Amsterdam center
        } else {
          const geocodedPosition = await geocodeAddress(addressString);
          position = geocodedPosition || { lat: 52.3676, lng: 4.9041 };
        }
      } catch (error) {
        console.log("Geocoding failed:", error);
        position = { lat: 52.3676, lng: 4.9041 };
      }
    }

    if (!position || typeof position.lat !== 'number' || typeof position.lng !== 'number' || isNaN(position.lat) || isNaN(position.lng)) {
      throw new Error("Invalid position for Street View");
    }

    // Check if component is still mounted before proceeding
    if (!isMountedRef.current) {
      return null;
    }

    // Check if Street View is available at this location
    const streetViewAvailable = await checkStreetViewAvailability(position);
    if (!isMountedRef.current) {
      return null;
    }

    if (!streetViewAvailable) {
      throw new Error("Street View is not available at this location");
    }

    // Parse initial data from URL, but ignore it if this is an address change 
    const initialData = (initialUrl && !isAddressChange) ? parseStreetViewUrl(initialUrl) : null;
    
    // Pre-calculate heading if we don't have initial data
    let calculatedHeading: number | null = null;
    if (!initialData) {
      const optimalData = await getOptimalPanoramaData(position);
      if (optimalData && isMountedRef.current) {
        calculatedHeading = optimalData.heading;
        position = optimalData.position;
      }
    }
    
    const streetViewOptions: google.maps.StreetViewPanoramaOptions = {
      position,
      ...(initialData && {
        pov: {
          heading: initialData.heading,
          pitch: initialData.pitch
        }
      }),
      ...(calculatedHeading !== null && !initialData && {
        pov: {
          heading: calculatedHeading,
          pitch: 0
        }
      }),
      zoom: initialData ? Math.max(0, Math.min(4, (120 - initialData.zoom) / 22.5)) : 1,
      visible: true,
      addressControl: false,
      linksControl: true,
      panControl: true,
      zoomControl: true,
      enableCloseButton: false,
      showRoadLabels: false
    };

    // Create Street View with timeout
    const streetView = await new Promise<google.maps.StreetViewPanorama>((resolve, reject) => {
      let isResolved = false;
      let fallbackTimeout: NodeJS.Timeout;
      
      const timeout = setTimeout(() => {
        if (isMountedRef.current && !isResolved) {
          isResolved = true;
          reject(new Error("Street View creation timeout"));
        }
      }, 10000);

      const cleanup = (): void => {
        clearTimeout(timeout);
        if (fallbackTimeout) {
          clearTimeout(fallbackTimeout);
        }
      };

      try {
        const sv = new google.maps.StreetViewPanorama(streetViewRef.current as HTMLElement, streetViewOptions);
        
        // Wait for the first panorama to load
        const loadListener = sv.addListener("pano_changed", () => {
          cleanup();
          if (google && google.maps && google.maps.event) {
            (google.maps.event as any).removeListener(loadListener);
          }
          if (isMountedRef.current && !isResolved) {
            isResolved = true;
            resolve(sv);
          }
        });

        // Fallback - if no pano_changed event fires, resolve after a delay
        fallbackTimeout = setTimeout(() => {
          if ((sv as any).getPano() && isMountedRef.current && !isResolved) {
            cleanup();
            (google.maps.event as any).removeListener(loadListener);
            isResolved = true;
            resolve(sv);
          }
        }, 3000);
        
      } catch (error) {
        cleanup();
        if (isMountedRef.current && !isResolved) {
          isResolved = true;
          reject(error);
        }
      }
    });

    if (!isMountedRef.current) {
      return null;
    }

    streetViewInstance.current = streetView;

    // Set up event listeners
    const debouncedUpdateUrl = pDebounce((): void => {
      if (!isMountedRef.current) return;
      
      const currentPosition = streetView.getPosition();
      const pov = streetView.getPov();
      const zoom = streetView.getZoom();

      if (currentPosition) {
        const fov = Math.max(10, Math.min(120, 120 - (zoom || 1) * 22.5));
        
        const data: StreetViewData = {
          location: { lat: currentPosition.lat(), lng: currentPosition.lng() },
          heading: pov.heading || 0,
          pitch: pov.pitch || 0,
          zoom: fov
        };
        
        const newUrl = generateStreetViewUrl(data);
        safeSetCurrentStreetViewUrl(newUrl);
        onPositionChange(newUrl);
      }
    }, 500);

    streetView.addListener("position_changed", debouncedUpdateUrl);
    streetView.addListener("pov_changed", debouncedUpdateUrl);
    streetView.addListener("zoom_changed", debouncedUpdateUrl);

    // Trigger initial URL update - capture the initial state without interfering with user interactions
    setTimeout(() => {
      if (isMountedRef.current) {
        const currentPosition = streetView.getPosition();
        const pov = streetView.getPov();
        const zoom = streetView.getZoom();

        if (currentPosition) {
          const fov = Math.max(10, Math.min(120, 120 - (zoom || 1) * 22.5));
          
          const data: StreetViewData = {
            location: { lat: currentPosition.lat(), lng: currentPosition.lng() },
            heading: pov.heading || 0,
            pitch: pov.pitch || 0,
            zoom: fov
          };
          
          const newUrl = generateStreetViewUrl(data);
          safeSetCurrentStreetViewUrl(newUrl);
          onPositionChange(newUrl);
        }
        
        if (onAddressChanged && !hasInitializedOnce) {
          onAddressChanged();
        }
      }
    }, 1000);

    return streetView;
    
  } catch (error) {
    safeSetInitializationError(error instanceof Error ? error.message : "Failed to load Street View");
    streetViewInstance.current = null;
    return null;
  } finally {
    initializationInProgressRef.current = false;
    safeSetIsInitializing(false);
  }
};

/**
 * Initialize Map with given parameters
 */
export const initializeMap = async (params: {
  apiLoaded: boolean;
  mapRef: React.RefObject<HTMLDivElement>;
  mapInstance: React.MutableRefObject<google.maps.Map | null>;
  markerInstance: React.MutableRefObject<google.maps.Marker | null>;
  isInitializing: boolean;
  address: { postcode?: string; huisnummer?: string; straat?: string; plaats?: string };
  isMountedRef: React.MutableRefObject<boolean>;
  initialUrl?: string;
  hasInitializedOnce: boolean;
  parseStreetViewUrl: (url: string) => StreetViewData | null;
  generateStreetViewUrl: (data: StreetViewData) => string;
  geocodeAddress: (address: string) => Promise<{ lat: number; lng: number } | null>;
  onPositionChange: (url: string) => void;
  safeSetCurrentStreetViewUrl: (value: string | null) => void;
}): Promise<google.maps.Map | null> => {
  const {
    apiLoaded, mapRef, mapInstance, markerInstance, isInitializing, address,
    isMountedRef, initialUrl, hasInitializedOnce, parseStreetViewUrl,
    generateStreetViewUrl, geocodeAddress, onPositionChange, safeSetCurrentStreetViewUrl
  } = params;
  
  if (!apiLoaded || !mapRef.current || mapInstance.current || isInitializing) {
    return null;
  }

  try {
    // Get initial position
    let position: { lat: number; lng: number } | null = null;
    
    // Try to parse initial position from URL (only on first initialization)
    if (initialUrl && !hasInitializedOnce) {
      const initialData = parseStreetViewUrl(initialUrl);
      if (initialData) {
        position = initialData.location;
      }
    }
    
    // If no position from URL, geocode the current address
    if (!position) {
      try {
        const addressString = `${address.straat || ''} ${address.huisnummer || ''}, ${address.postcode || ''} ${address.plaats || ''}`.trim();
        
        if (!address.postcode || !address.huisnummer) {
          position = { lat: 52.3676, lng: 4.9041 }; // Amsterdam center
        } else {
          const geocodedPosition = await geocodeAddress(addressString);
          position = geocodedPosition || { lat: 52.3676, lng: 4.9041 };
        }
      } catch (error) {
        position = { lat: 52.3676, lng: 4.9041 };
      }
    }

    if (!position || typeof position.lat !== 'number' || typeof position.lng !== 'number' || isNaN(position.lat) || isNaN(position.lng)) {
      return null;
    }

    const mapOptions: google.maps.MapOptions = {
      center: position,
      zoom: 17,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: true,
      mapTypeControl: true,
      fullscreenControl: false
    };

    const map = new google.maps.Map(mapRef.current, mapOptions);
    mapInstance.current = map;

    // Create draggable marker
    const marker = new google.maps.Marker({
      position,
      map,
      draggable: true,
      title: "Drag to change position"
    });
    markerInstance.current = marker;

    // Set up event listeners with debouncing
    const debouncedUpdateUrlFromMap = pDebounce((): void => {
      if (!isMountedRef.current) return;
      
      const markerPosition = marker.getPosition();
      if (markerPosition) {
        const data: StreetViewData = {
          location: { lat: markerPosition.lat(), lng: markerPosition.lng() },
          heading: 0,
          pitch: 0,
          zoom: 90
        };
        
        const newUrl = generateStreetViewUrl(data);
        safeSetCurrentStreetViewUrl(newUrl);
        onPositionChange(newUrl);
      }
    }, 300);

    marker.addListener("dragend", debouncedUpdateUrlFromMap);
    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        marker.setPosition(e.latLng);
        debouncedUpdateUrlFromMap().catch(() => {});
      }
    });

    // Trigger initial URL update
    setTimeout(() => {
      const debouncedMapInitialUpdate = pDebounce(() => {
        if (isMountedRef.current) {
          debouncedUpdateUrlFromMap().catch(() => {});
        }
      }, 500);
      
      void debouncedMapInitialUpdate().catch(() => {});
    }, 1000);

    return map;
  } catch (error) {
    console.log("Failed to initialize Map:", error);
    return null;
  }
};

/**
 * Create safe state setter functions to prevent memory leaks
 */
export const createSafeStateSetter = <T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  isMountedRef: React.MutableRefObject<boolean>
) => {
  return (value: T | ((prev: T) => T)) => {
    if (isMountedRef.current) {
      setState(value);
    }
  };
};

/**
 * Update Street View panorama to look towards a target position with optimal heading
 */
export const updateStreetViewPosition = async (
  streetViewInstance: google.maps.StreetViewPanorama,
  targetPosition: { lat: number; lng: number },
  onPositionUpdate?: (position: { lat: number; lng: number }) => void
): Promise<void> => {
  if (!(window as any).google?.maps?.StreetViewService) {
    // Fallback if StreetViewService is not available
    streetViewInstance.setPosition(targetPosition);
    onPositionUpdate?.(targetPosition);
    return;
  }

  const streetViewService = new (window as any).google.maps.StreetViewService();
  const point = new (window as any).google.maps.LatLng(targetPosition.lat, targetPosition.lng);
  const streetViewMaxDistance = 50; // meters
  
  return new Promise<void>((resolve) => {
    streetViewService.getPanoramaByLocation(point, streetViewMaxDistance, (streetViewPanoramaData: any, status: any) => {
      if (status === (window as any).google.maps.StreetViewStatus.OK && streetViewPanoramaData?.location?.latLng) {
        const actualPanoramaPoint = streetViewPanoramaData.location.latLng;
        
        // Calculate heading from panorama position toward the target address (the building)
        if ((window as any).google?.maps?.geometry?.spherical) {
          // Use the original geocoded address point as the target to look at
          const targetPoint = new (window as any).google.maps.LatLng(targetPosition.lat, targetPosition.lng);
          const heading = (window as any).google.maps.geometry.spherical.computeHeading(actualPanoramaPoint, targetPoint);
          
          // Set position and PoV
          streetViewInstance.setPosition(actualPanoramaPoint);
          streetViewInstance.setPov({
            heading: heading,
            pitch: 0
          });
          streetViewInstance.setZoom(1);
          
          console.log("Street view PoV updated with heading:", heading);
          console.log("Panorama position:", { lat: actualPanoramaPoint.lat(), lng: actualPanoramaPoint.lng() });
          console.log("Target address position:", { lat: targetPosition.lat, lng: targetPosition.lng });
          console.log("Distance between panorama and target:", 
            (window as any).google.maps.geometry.spherical.computeDistanceBetween(actualPanoramaPoint, targetPoint), "meters");
          
          // Notify about the actual panorama position
          onPositionUpdate?.({ lat: actualPanoramaPoint.lat(), lng: actualPanoramaPoint.lng() });
        } else {
          // No geometry API available, just set the position
          streetViewInstance.setPosition(actualPanoramaPoint);
          onPositionUpdate?.({ lat: actualPanoramaPoint.lat(), lng: actualPanoramaPoint.lng() });
        }
      } else {
        console.log("Street View is not available at this location");
        // Fallback to direct position setting
        streetViewInstance.setPosition(targetPosition);
        onPositionUpdate?.(targetPosition);
      }
      resolve();
    });
  });
};

/**
 * Get encoded address string for geocoding
 */
export const getAddressString = (address: { 
  straat?: string; 
  huisnummer?: string; 
  postcode?: string; 
  plaats?: string; 
}): string => {
  return `${address.straat || ''} ${address.huisnummer || ''}, ${address.postcode || ''} ${address.plaats || ''}`.trim();
};

export default useGoogleMapsAPI;
